import React, { createContext, useContext, useEffect, useState } from "react";
import client from "@/api";
import { ASSETS, CONTACT } from "@/mock";
import { setPdfBrand } from "@/lib/invoicePdf";

const TenantContext = createContext(null);
// Hard-coded platform palette that white-label tenants re-skin.
const PRIMARY = ["#1B1F5B", "#141844", "#0A0F2E"];
const SECONDARY = ["#F5B32B", "#E89A10", "#E0A41F"];
const PROPS = { bg: "background-color", text: "color", border: "border-color", fill: "fill", stroke: "stroke", ring: "--tw-ring-color" };
const VARIANTS = ["", "hover\\:", "focus\\:", "group-hover\\:"];
const PSEUDO = { "": "", "hover\\:": ":hover", "focus\\:": ":focus", "group-hover\\:": "" };

const shade = (hex, pct) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c * (1 + pct))));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => f(c).toString(16).padStart(2, "0")).join("")}`;
};

const buildCss = (primary, secondary) => {
  const map = {};
  [primary, shade(primary, -0.25), shade(primary, -0.55)].forEach((c, i) => (map[PRIMARY[i]] = c));
  [secondary, shade(secondary, -0.12), shade(secondary, -0.08)].forEach((c, i) => (map[SECONDARY[i]] = c));
  let css = `:root{--brand-primary:${primary};--brand-secondary:${secondary};}`;
  Object.entries(map).forEach(([from, to]) => {
    [from.toUpperCase(), from.toLowerCase()].forEach((hex) => {
      Object.entries(PROPS).forEach(([p, prop]) => {
        VARIANTS.forEach((v) => {
          const sel = `.${v}${p}-\\[\\${hex}\\]${PSEUDO[v]}`;
          const full = v === "group-hover\\:" ? `.group:hover ${sel}` : sel;
          css += `${full}{${prop}:${to} !important}`;
        });
      });
      css += `.from-\\[\\${hex}\\]{--tw-gradient-from:${to} !important;--tw-gradient-stops:var(--tw-gradient-from),var(--tw-gradient-to,transparent) !important}`;
      css += `.to-\\[\\${hex}\\]{--tw-gradient-to:${to} !important}`;
    });
  });
  return css;
};

export const assetUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

// Admin "preview as agency" override: per-tab only (sessionStorage) so it never sticks on the real site.
localStorage.removeItem("ft_tenant_host");
export const previewHost = () => {
  const q = new URLSearchParams(window.location.search).get("tenant");
  if (q !== null) {
    if (q) sessionStorage.setItem("ft_tenant_host", q);
    else sessionStorage.removeItem("ft_tenant_host");
  }
  return sessionStorage.getItem("ft_tenant_host");
};
export const tenantHost = () => previewHost() || window.location.host;

const PreviewBar = ({ name }) => (
  <div className="fixed bottom-4 left-1/2 z-[100] flex -translate-x-1/2 items-center gap-3 rounded-full bg-black/85 px-4 py-2 text-xs text-white shadow-xl" data-testid="tenant-preview-bar">
    <span>Previewing <b>{name}</b></span>
    <button onClick={() => { sessionStorage.removeItem("ft_tenant_host"); window.location.href = window.location.pathname; }}
      className="rounded-full bg-white px-3 py-1 font-bold text-black" data-testid="tenant-preview-exit">Exit preview</button>
  </div>
);

client.interceptors.request.use((config) => {
  config.headers["X-Tenant-Host"] = tenantHost();
  return config;
});

const platformBrand = { name: "FLYTRIP", logo: ASSETS.logo, company: CONTACT.company, isWhiteLabel: false, contact: CONTACT };

export const TenantProvider = ({ children }) => {
  const [brand, setBrand] = useState(platformBrand);
  const [rootDomain, setRootDomain] = useState("");

  const apply = (t) => {
    if (!t || t.is_platform) {
      document.getElementById("tenant-theme")?.remove();
      setPdfBrand({ name: "FLYTRIP", primary: "#1B1F5B", secondary: "#F5B32B" });
      setBrand(platformBrand);
      return;
    }
    let el = document.getElementById("tenant-theme");
    if (!el) {
      el = document.createElement("style");
      el.id = "tenant-theme";
      document.head.appendChild(el);
    }
    el.textContent = buildCss(t.primary_color, t.secondary_color);
    setPdfBrand({ name: t.name, primary: t.primary_color, secondary: t.secondary_color });
    document.title = `${t.name} — Flights, Hotels & Holidays`;
    const ci = t.contact_info || {};
    setBrand({
      tenant: t, name: t.name, logo: assetUrl(t.logo_url) || null, company: t.name, isWhiteLabel: true,
      contact: {
        ...CONTACT, email: ci.email || "", phone: (ci.phone || "").replace(/\D/g, "").slice(-10),
        phonePretty: ci.phone || "", address: ci.address || "", company: t.name, whatsapp: ci.whatsapp || "",
      },
    });
  };

  const refresh = () => client.get("/tenant/current").then((r) => { setRootDomain(r.data.root_domain || ""); apply(r.data.tenant); }).catch(() => {});

  useEffect(() => { refresh(); }, []);

  return (
    <TenantContext.Provider value={{ ...brand, rootDomain, refresh, applyTheme: apply }}>
      {children}
      {previewHost() && <PreviewBar name={brand.name} />}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext) || platformBrand;

export const BrandLogo = ({ className, testId }) => {
  const b = useTenant();
  if (b.logo) return <img src={b.logo} alt={b.name} className={className} data-testid={testId} />;
  return (
    <span data-testid={testId} className="font-display text-2xl font-extrabold tracking-tight text-[#1B1F5B]">
      {b.name}
    </span>
  );
};
